import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import EditIcon from '@mui/icons-material/Edit';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';

import DataTable from '../../../../components/common/datatable/DataTable'
import ButtonComponent from '../../../../components/common/button/Button';

const DataDetails = ({rowData, onSelectionChanged, rowSelection, handleCellValueChange, getRowStyle, userRole}) => {
  const [isGridReady, setIsGridReady] = useState(false);
  const [columnState, setColumnState] = useState(null);
  const [disableFilterBtn, setDisableFilterBtn] = useState(true);
  const [selectedColumn, setSelectedColumn] = useState('');
  const currentPageRef = useRef(0);  

  const pagination = true;
  const paginationPageSize = Number(localStorage.getItem("cmPageSize")) || 500;
  const paginationPageSizeSelector = [500, 1000, 2000, 5000];

  const isInvalidValue = (value, status) => {
    return status.toLowerCase() === "rejected" && (value === null || value === "");
  };

  const renderExpandableText = (params, maxLength = 50) => {
    const text = params.value || ""; // The error details text
    const isLongText = text.length > maxLength;

    // Initialize the showFullText property for the row if not already set
    if (!params.node.showFullText) {
      params.node.showFullText = false;
    }

    const toggleText = () => {
      params.node.showFullText = !params.node.showFullText;
      params.api.refreshCells({ rowNodes: [params.node], force: true }); // Refresh the cell to update the UI
    };

    return isLongText ? (
      <div>
        {params.node.showFullText ? (
          <>
            <span>{text}</span>{" "}
            <button
              onClick={toggleText}
              style={{
                background: "none",
                border: "none",
                color: "#181d1f",
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: "bold",
              }}
            >
              Less
            </button>
          </>
        ) : (
          <>
            <span>{text.substring(0, maxLength)}...</span>{" "}
            <button
              onClick={toggleText}
              style={{
                background: "none",
                border: "none",
                color: "#181d1f",
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: "bold",
              }}
            >
              More
            </button>
          </>
        )}
      </div>
    ) : (
      <span>{text}</span>
    );
  };

  const headerItems = useMemo(() => {
      return [
            {
              headerName: "Data Date Time Stamp",
              field: "dataDateTimeStamp",
              sortable: true,
              unSortIcon: true,
            },
            {
              headerName: "AL Serial Number",
              field: "alSerialNumber",
              sortable: true,
              unSortIcon: true,
            },
            {
              headerName: "Agreement Number",
              field: "agreementNumber",
              sortable: true,
              unSortIcon: true,
            },
            {
              headerName: "New Target Value",
              field: "newTargetValue",
              sortable: true,
              unSortIcon: true,
            },
            {
              headerName: "Old Target Value",
              field: "oldTargetValue",
              sortable: true,
              unSortIcon: true,
            },
            {
              headerName: "Agreement Date",
              field: "agreementDate",
              sortable: true,
              unSortIcon: true,
            },
            {
              headerName: "Validity Start Date",
              field: "validityStartDate",
              sortable: true,
              unSortIcon: true,
            },
            {
              headerName: "Validity End Date",
              field: "validityEndDate",
              sortable: true,
              unSortIcon: true,
            },
            {
              headerName: "Plant",
              field: "plant",
              sortable: true,
              unSortIcon: true,
            },
            {
              headerName: "Material Number",
              field: "materialNumber",
              sortable: true,
              unSortIcon: true,
            },
            {
              headerName: "Vendor Number",
              field: "vendorNumber",
              sortable: true,
              unSortIcon: true,
            },
            {
              headerName: "Update Timestamp",
              field: "updateTimestamp",
              sortable: true,
              unSortIcon: true,
            },
            {
              headerName: "Username",
              field: "username",
              sortable: true,
              unSortIcon: true,
            },
            {
              headerName: "Reason if Rejected",
              field: "reasonIfRejected",
              sortable: true,
              resizable: true,
              optionalField: true,
              unSortIcon: true,
                editable: userRole === "Viewer" ? false : true,
                cellClass: userRole !== "Viewer" ? "cpsComments-cell" : "",
                valueSetter: (params) => {
                    if (params.newValue !== params.oldValue) {
                    const dataCopy = { ...params.data, reasonIfRejected: params.newValue };
                    params.node.setData(dataCopy);
                    return true;
                    }
                    return false;
                },
                cellRenderer: (params) => {
                    return (
                    <div className="cps-comments-icon">
                        <span style={{ flex: 1, padding: "0px 4px" }}>
                        {params.value}{" "}
                        </span>
                        {userRole !== "Viewer" && <EditIcon fontSize="sm" />}
                    </div>
                    );
                },cellClassRules: {
                    "red-cell": (params) => isInvalidValue(params.value ,params.data.status),
                },
            },
            {
              headerName: "Errors",
              field: "errorDetails",
              width: 180,
              sortable: true,
              resizable: true,
              unSortIcon: true,
              cellRenderer: (params) => renderExpandableText(params, 50),
            },
            {
              headerName: "Status",
              field: "status",
              sortable: true,
              optionalField: true,
              resizable: false,
              initialWidth: 180,
              pinned: "right",
              wrapText: true,
              autoHeight: true,
              cellClass: "paymentTerms-cell",
              unSortIcon: true,
            },
          ];
    }, []);

    const selectionColumnDef = useMemo(() => ({
        pinned: "left",
    }), []);

    const gridApiRef = useRef(null);

    const onColumnChange = e => {
        const col = e.target.value;
        setSelectedColumn(col);
        if (col && gridApiRef.current) {
        gridApiRef.current.ensureColumnVisible(col);
        }
    };

    const onPaginationChanged = () => {
        if (gridApiRef.current && !gridApiRef.current.isDestroyed()) {
        const currentPage = gridApiRef.current.paginationGetCurrentPage();
        const pageSize = gridApiRef.current.paginationGetPageSize();
        currentPageRef.current = currentPageRef.current || currentPage;
        localStorage.setItem("lastVisitedPage", currentPage);
        localStorage.setItem("cmPageSize", pageSize);
        }
    };

    const onGridReady = (params) => {
        gridApiRef.current = params.api; // Store the grid API instance
        setIsGridReady(true);
        if (columnState) {
        params.columnApi.applyColumnState({ state: columnState, applyOrder: true });
        }
    };

    const onFirstDataRendered = () => {
        if (gridApiRef.current && !gridApiRef.current.isDestroyed()) {
        gridApiRef.current.paginationGoToPage(currentPageRef.current || 0);
        localStorage.setItem("lastVisitedPage", currentPageRef.current);
        }
    };

    useEffect(() => {
        if (isGridReady && gridApiRef?.current) {
            const savedFilterModel = localStorage.getItem("autoTargetReviewValue");
            if (savedFilterModel) {
            gridApiRef.current.setFilterModel(JSON.parse(savedFilterModel));
            setDisableFilterBtn(true);
            }
        }
    }, [isGridReady]);

    const clearAllFilters = () => {
        if (gridApiRef.current) {
        gridApiRef.current.setFilterModel(null); // Clear all filters
        localStorage.removeItem("autoTargetReviewValue");
        setDisableFilterBtn(true);
        }
    };

    const onFilterChanged = () => {
        if (gridApiRef.current) {
        const filterModel = gridApiRef.current.getFilterModel();
        setDisableFilterBtn(Object.keys(filterModel).length === 0); // Enable button if filters exist
        localStorage.setItem("autoTargetReviewValue", JSON.stringify(filterModel));
        }
    };

    const onColumnMoved = useCallback((params) => {
        if (params && params.columnApi && typeof params.columnApi.getColumnState === "function") {
            setColumnState(params.columnApi.getColumnState());
        }
        }, []);

        const gridOptions = {
        getRowId: (params) => {
        return `${params?.data?.alSeqId}`
        },
        overlayLoadingTemplate: `<span class="ag--overlay-loading-center" >Please wait while data is loading</span>`,
        overlayNoRowsTemplate: `<span class="ag--overlay-no-rows-center" >No Data Available</span>`,
        defaultColDef: {
        sortable: true,
        autoHeight: true,
        filter: 'agMultiColumnFilter',
        cellStyle: {
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: 1.5,
            display: "flex",
            alignItems: "center",
        },
        valueGetter: (params) => {
            if (!params.data) return "";
  
            const field = params.colDef.field;
            const value = params.data[field];
  
            return value === null || value === undefined || value === "" ? "" : value;
        },
        },
        onColumnMoved,
        onFilterChanged,
    }

  return (
    <>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <FormControl sx={{ m: 1, minWidth: 200 }} size="small">
                <InputLabel>Jump to Column</InputLabel>
                <Select
                id="col-select"
                value={selectedColumn}
                label="Jump to Column"
                onChange={onColumnChange}
                >
                <MenuItem value="">Select Column</MenuItem>
                {headerItems?.flatMap(col =>
                    col.children
                    ? col.children.map(child => (
                        <MenuItem key={child.field} value={child.field}>
                            {child.headerName || child.field}
                        </MenuItem>
                        ))
                    : [
                        <MenuItem key={col.field} value={col.field}>
                            {col.headerName || col.field}
                        </MenuItem>
                        ]
                )}
                </Select>
            </FormControl>
            <ButtonComponent
                maxWidth="150px"
                label="Clear All Filters"
                bgColor="black"
                color="white"
                disabled={disableFilterBtn}
                onClick={clearAllFilters}
            />
        </div>
        {
            rowData && 
            <DataTable
                height={750}
                columns={headerItems}
                rows={rowData || []}
                paginationProp={pagination}
                paginationPageSize={paginationPageSize}
                paginationPageSizeSelector={paginationPageSizeSelector}
                selectionColumnDef={selectionColumnDef}
                rowSelection={rowSelection}
                onSelectionChanged={onSelectionChanged}
                onGridReady={onGridReady}
                gridOptions={gridOptions}
                onCellValueChanged={handleCellValueChange}
                onPaginationChanged={onPaginationChanged}
                onFirstDataRendered={onFirstDataRendered}
                getRowStyle={getRowStyle}
            />
        }
    </>
  )
}

export default DataDetails